/**
 * Безопасная обработка аудио для учебных записей
 * Приоритет: НЕ НАВРЕДИТЬ качеству речи
 */
 
import { useRef, useCallback } from 'react';
import { handleError } from '../../context/errors/errorUtils';

export const useAudioProcessor = (silenceThreshold = 0.005, minSilenceDuration = 300, isNative = false) => {
  const audioContextRef = useRef(null);
  
  /**
   * Консервативное удаление тишины - только длинные паузы
   */
	const removeSilenceConservative = (audioData, sampleRate, threshold = 0.005, minSilenceMs = 100) => {
	  if (audioData.length === 0) return audioData;

	  if (audioData.length / sampleRate < 2) {
		return audioData;
	  }

	  const minSilenceSamples = Math.floor(minSilenceMs * sampleRate / 1000);
	  const maxSilenceKeep = Math.floor(sampleRate * 0.1); // 100мс
	  const windowSize = Math.floor(sampleRate * 0.05); // 50мс

	  const silenceMap = new Array(audioData.length).fill(false);

	  for (let i = 0; i < audioData.length; i += windowSize) {
		const end = Math.min(i + windowSize, audioData.length);
		let silentCount = 0;

		for (let j = i; j < end; j++) {
		  if (Math.abs(audioData[j]) < threshold) silentCount++;
		}

		if (silentCount / (end - i) > 0.95) {
		  for (let j = i; j < end; j++) silenceMap[j] = true;
		}
	  }

	  // Находим сегменты
	  const segments = [];
	  let currentStart = 0;
	  let isCurrentSilent = silenceMap[0];

	  for (let i = 1; i <= audioData.length; i++) {
		const isCurrentSampleSilent = i < audioData.length ? silenceMap[i] : !isCurrentSilent;

		if (isCurrentSampleSilent !== isCurrentSilent || i === audioData.length) {
		  segments.push({ start: currentStart, end: i, isSilent: isCurrentSilent });
		  currentStart = i;
		  isCurrentSilent = isCurrentSampleSilent;
		}
	  }

	  // Определяем границы речи
	  const firstSpeechIdx = segments.findIndex(s => !s.isSilent);
	  const lastSpeechIdx = (() => {
		for (let i = segments.length - 1; i >= 0; i--) {
		  if (!segments[i].isSilent) return i;
		}
		return -1;
	  })();

	  // Если речи вообще нет — возвращаем как есть
	  if (firstSpeechIdx === -1) return audioData;

	  const processedSegments = [];

	  for (let idx = 0; idx < segments.length; idx++) {
		const segment = segments[idx];
		const segmentLength = segment.end - segment.start;

		if (!segment.isSilent) {
		  processedSegments.push(segment);
		  continue;
		}

		// Краевая тишина (до первой речи или после последней) — удаляем полностью
		if (idx < firstSpeechIdx || idx > lastSpeechIdx) {
		  continue;
		}

		// Короткая пауза в середине — сохраняем
		if (segmentLength < minSilenceSamples) {
		  processedSegments.push(segment);
		  continue;
		}

		// Длинная пауза в середине — сжимаем до 100мс
		const keepLength = Math.min(segmentLength, maxSilenceKeep);
		const startKeep = segment.start + Math.floor((segmentLength - keepLength) / 2);

		processedSegments.push({
		  start: startKeep,
		  end: startKeep + keepLength,
		  isSilent: true
		});
	  }

	  const totalLength = processedSegments.reduce((sum, seg) => sum + (seg.end - seg.start), 0);
	  const result = new Float32Array(totalLength);
	  let offset = 0;

	  for (const segment of processedSegments) {
		const segmentData = audioData.slice(segment.start, segment.end);
		result.set(segmentData, offset);
		offset += segmentData.length;
	  }

	  return result;
	};
  
  /**
   * Очень осторожная нормализация - только если действительно нужно
   */
  const normalizeAudioSafe = (audioData) => {
    if (audioData.length === 0) return audioData;
    
    let maxAmplitude = 0;
    for (let i = 0; i < audioData.length; i++) {
      maxAmplitude = Math.max(maxAmplitude, Math.abs(audioData[i]));
    }
    
    // Если аудио совсем тихое (< 5%) или уже достаточно громкое (> 15%) - не трогаем
    if (maxAmplitude === 0 || maxAmplitude > 0.15) {
      //console.log(`[SafeAudio] Skipping normalization, max amplitude: ${maxAmplitude.toFixed(3)}`);
      return audioData;
    }
    
    // Очень консервативное усиление - максимум в 3 раза
    const targetLevel = 0.3; // Безопасный уровень
    const scale = targetLevel / maxAmplitude;
    const maxGain = 3; // Максимум 3x усиление
    const finalScale = Math.min(scale, maxGain);
    
    //console.log(`[SafeAudio] Applying gentle normalization: ${finalScale.toFixed(2)}x`);
    
    const normalized = new Float32Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
      // Мягкое ограничение
      const amplified = audioData[i] * finalScale;
      normalized[i] = Math.max(-0.8, Math.min(0.8, amplified));
    }
    
    return normalized;
  };
  
  /**
   * Основная функция - максимально безопасная обработка
   */
  const processAudioComplete = useCallback(async (audioBlob) => {
    try {
      if (!audioBlob || audioBlob.size === 0) {
        return isNative 
          ? { audioData: new Float32Array(0), duration: 0 }
          : { wavBlob: new Blob([], { type: 'audio/wav' }), duration: 0 };
      }
      
      // 1. Декодируем
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      const originalAudioData = audioBuffer.getChannelData(0);
      
      //console.log(`[SafeAudio] Processing ${audioBuffer.duration.toFixed(2)}s audio`);
      
      // 2. Для очень коротких записей - минимальная обработка
      if (audioBuffer.duration < 1) {
        //console.log('[SafeAudio] Very short audio, minimal processing');
        const normalizedData = normalizeAudioSafe(originalAudioData);
        const wavBlob = createWavBlob(normalizedData, audioBuffer.sampleRate);
        
        return isNative 
          ? { audioData: normalizedData, wavBlob, duration: audioBuffer.duration }
          : { wavBlob, duration: audioBuffer.duration };
      }
      
      // 3. Консервативное удаление тишины (только для длинных записей)
      let processedData = originalAudioData;
      if (audioBuffer.duration > 3) {
        console.log(`[SafeAudio] Processing long recording (${audioBuffer.duration.toFixed(2)}s), applying silence removal...`);
        const withoutSilence = removeSilenceConservative(originalAudioData, audioBuffer.sampleRate, 0.005, 100);
        
        // Проверяем, что не удалили слишком много (не более 70% от оригинала = сохранили минимум 30%)
        const reductionRatio = withoutSilence.length / originalAudioData.length;
        console.log(`[SafeAudio] Silence removal reduction ratio: ${(reductionRatio * 100).toFixed(1)}% kept`);
        
        if (reductionRatio > 0.3) {
          processedData = withoutSilence;
          console.log(`[SafeAudio] Applied silence removal, reduction: ${((1-reductionRatio)*100).toFixed(1)}%`);
        } else {
          console.log('[SafeAudio] Silence removal too aggressive (removed >30%), keeping original');
          processedData = originalAudioData;
        }
      }
      
      // 4. Осторожная нормализация
      const normalizedData = normalizeAudioSafe(processedData);
      
      // 5. Ресемплинг только если действительно нужно
      let finalData = normalizedData;
      let finalSampleRate = audioBuffer.sampleRate;
      
      // Ресемплируем только если частота сильно отличается от 16kHz
      if (audioBuffer.sampleRate > 24000 || audioBuffer.sampleRate < 12000) {
        try {
          const tempBuffer = audioContextRef.current.createBuffer(1, normalizedData.length, audioBuffer.sampleRate);
          tempBuffer.getChannelData(0).set(normalizedData);
          
          const resampledBuffer = await resampleTo16kHz(tempBuffer);
          finalData = resampledBuffer.getChannelData(0);
          finalSampleRate = resampledBuffer.sampleRate;
          
          //console.log(`[SafeAudio] Resampled from ${audioBuffer.sampleRate}Hz to ${finalSampleRate}Hz`);
        } catch (error) {
          console.warn('[SafeAudio] Resampling failed, keeping original sample rate');
        }
      }
      
      const finalDuration = finalData.length / finalSampleRate;
      const wavBlob = createWavBlob(finalData, finalSampleRate);
      
      //console.log(`[SafeAudio] Final duration: ${finalDuration.toFixed(2)}s`);
      
      return isNative 
        ? { audioData: finalData, wavBlob, duration: finalDuration }
        : { wavBlob, duration: finalDuration };
        
    } catch (error) {
      console.error('[SafeAudio] Processing error:', error);
      
      // В случае ошибки возвращаем что-то безопасное
      return isNative 
        ? { audioData: new Float32Array(0), duration: 0 }
        : { wavBlob: new Blob([], { type: 'audio/wav' }), duration: 0 };
    }
  }, [isNative]);
  
  // Вспомогательные функции (те же что в оригинале)
  const createWavBlob = useCallback((audioData, sampleRate) => {
    const buffer = new ArrayBuffer(44 + audioData.length * 2);
    const view = new DataView(buffer);
    
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 32 + audioData.length * 2, true);
    writeString(view, 8, 'WAVE');
    
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    
    writeString(view, 36, 'data');
    view.setUint32(40, audioData.length * 2, true);
    
    let offset = 44;
    for (let i = 0; i < audioData.length; i++) {
      const sample = Math.max(-1, Math.min(1, audioData[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
    
    return new Blob([view], { type: 'audio/wav' });
  }, []);
  
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  const resampleTo16kHz = useCallback(async (audioBuffer) => {
    const targetSampleRate = 16000;
    
    if (audioBuffer.sampleRate === targetSampleRate) {
      return audioBuffer;
    }
    
    const offlineCtx = new OfflineAudioContext(
      1,
      audioBuffer.duration * targetSampleRate,
      targetSampleRate
    );
    
    const source = offlineCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineCtx.destination);
    source.start(0);
    
    return await offlineCtx.startRendering();
  }, []);
  
  return {
    processAudioComplete
  };
};
